import { Controller } from '@hotwired/stimulus';

const POLL_INTERVAL_MS = 2000;
const TERMINAL_STATUSES = ['completed', 'failed', 'cancelled'];

export default class extends Controller {
    static targets = [
        'token',
        'submit',
        'banner',
        'panel',
        'bar',
        'percent',
        'label',
        'step',
        'log',
        'summary',
    ];

    static values = {
        startUrl: String,
        statusUrl: String,
        activeJob: String,
    };

    connect() {
        this.jobId = null;
        this.timer = null;
        this.renderedLogs = 0;

        // Une synchro est deja en cours (lancee avant, ou depuis un autre onglet) : on reprend le suivi.
        if (this.activeJobValue) {
            this._follow(this.activeJobValue);
        }
    }

    disconnect() {
        this._stopPolling();
    }

    async launch(event) {
        event.preventDefault();

        if (this.jobId) return;

        this._setRunning(true);

        const body = new FormData();
        body.append('_token', this.tokenTarget.value);

        try {
            const response = await fetch(this.startUrlValue, {
                method: 'POST',
                body,
                headers: { Accept: 'application/json' },
            });
            const data = await response.json();

            if (!response.ok || !data.jobId) {
                this._reset();
                this._appendLog('error', data.message || 'Impossible de lancer la synchronisation.');
                this._setRunning(false);
                return;
            }

            this._follow(data.jobId);
        } catch {
            this._reset();
            this._appendLog('error', 'Impossible de joindre le serveur.');
            this._setRunning(false);
        }
    }

    _follow(jobId) {
        this.jobId = jobId;
        this._reset();
        this._setRunning(true);
        this._poll();
    }

    async _poll() {
        if (!this.jobId) return;

        try {
            const response = await fetch(this.statusUrlValue.replace('__JOB__', this.jobId), {
                headers: { Accept: 'application/json' },
            });
            const data = await response.json();

            if (!response.ok) {
                this._finish('error', data.message || 'Tâche introuvable.');
                return;
            }

            this._render(data);

            if (TERMINAL_STATUSES.includes(data.status)) {
                this._finish(null, null, data);
                return;
            }
        } catch {
            // Coupure reseau passagere : la synchro continue sur le serveur, on retente au prochain tour.
        }

        this.timer = setTimeout(() => this._poll(), POLL_INTERVAL_MS);
    }

    _render(state) {
        this.barTarget.style.width = `${state.percent}%`;
        this.percentTarget.textContent = `${state.percent} %`;
        this.labelTarget.textContent = state.label;
        this.stepTarget.textContent = state.total > 0 ? `Étape ${state.current} / ${state.total}` : '';

        const logs = state.logs ?? [];
        // Le serveur ne garde que les dernieres lignes : on repart de zero si la liste a ete tronquee.
        if (logs.length < this.renderedLogs) {
            this.logTarget.innerHTML = '';
            this.renderedLogs = 0;
        }
        for (const log of logs.slice(this.renderedLogs)) {
            this._appendLog(log.level, log.message);
        }
        this.renderedLogs = logs.length;
    }

    _finish(level, message, state = null) {
        if (message) {
            this._appendLog(level, message);
        }

        if (state?.status === 'completed' && state.result) {
            this.barTarget.style.width = '100%';
            this.percentTarget.textContent = '100 %';
            this._renderSummary(state.result);
        } else if (state?.error) {
            this.labelTarget.textContent = state.error;
        }

        this._stopPolling();
        this.jobId = null;
        this._setRunning(false);
    }

    _reset() {
        this.logTarget.innerHTML = '';
        this.renderedLogs = 0;
        this.summaryTarget.hidden = true;
        this.summaryTarget.innerHTML = '';
        this.barTarget.style.width = '0%';
        this.percentTarget.textContent = '0 %';
        this.labelTarget.textContent = 'Démarrage...';
        this.stepTarget.textContent = '';
        this.panelTarget.hidden = false;
    }

    _setRunning(running) {
        this.submitTarget.disabled = running;
        this.bannerTarget.hidden = !running;
    }

    _stopPolling() {
        if (this.timer) {
            clearTimeout(this.timer);
            this.timer = null;
        }
    }

    _appendLog(level, message) {
        const line = document.createElement('div');
        line.className = `ovh-sync__log-line ovh-sync__log-line--${level}`;
        line.textContent = message;
        this.logTarget.appendChild(line);
        this.logTarget.scrollTop = this.logTarget.scrollHeight;
    }

    _renderSummary(result) {
        const domain = result.domain;
        const hosting = result.hosting;
        const errors = result.errors ?? [];

        let html = '<ul class="ovh-sync__summary-list">';
        html += `<li><strong>Domaines</strong> — créés : ${domain.created}, mis à jour : ${domain.updated}, ignorés : ${domain.skipped}, résiliés : ${domain.marked_resiliated}, supprimés : ${domain.pruned}</li>`;
        html += `<li><strong>Hébergements</strong> — créés : ${hosting.created}, mis à jour : ${hosting.updated}, ignorés : ${hosting.skipped}, résiliés : ${hosting.marked_resiliated}</li>`;
        if (result.mxplan) {
            html += `<li><strong>MX Plan</strong> — créés : ${result.mxplan.created}, mis à jour : ${result.mxplan.updated}, résiliés : ${result.mxplan.marked_resiliated}</li>`;
        }
        html += '</ul>';

        if (errors.length > 0) {
            html += '<p class="ovh-sync__summary-warnings"><strong>Avertissements :</strong></p><ul class="ovh-sync__summary-list">';
            for (const error of errors) {
                html += `<li>${this._escapeHtml(error)}</li>`;
            }
            html += '</ul>';
        }

        this.summaryTarget.innerHTML = html;
        this.summaryTarget.hidden = false;
    }

    _escapeHtml(text) {
        return text
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;');
    }
}
